
import { getPdfSelectionDetails } from "./pdfSelectionUtils";
import { IS_APPLE_TOUCH } from "../../utils/applePencil";

const DEVANAGARI_RE = /[\u0900-\u097F]/;

const buildSelectionOcrImage = (canvas, rect, canvasRect) => {
  if (!canvas || !canvasRect) return null;

  const scaleX = canvas.width / canvasRect.width;
  const scaleY = canvas.height / canvasRect.height;
  const cropX = Math.max(0, (rect.left - canvasRect.left) * scaleX);
  const cropY = Math.max(0, (rect.top - canvasRect.top) * scaleY);
  const cropW = Math.min(canvas.width - cropX, rect.width * scaleX);
  const cropH = Math.min(canvas.height - cropY, rect.height * scaleY);

  if (cropW <= 2 || cropH <= 2) return null;

  const sourceCtx = canvas.getContext("2d", { willReadFrequently: true });
  if (!sourceCtx) return null;

  const imageData = sourceCtx.getImageData(cropX, cropY, cropW, cropH);
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = cropW;
  tempCanvas.height = cropH;
  tempCanvas.getContext("2d", { willReadFrequently: true })?.putImageData(imageData, 0, 0);
  return tempCanvas.toDataURL("image/png");
};

// Builds a workspace text snippet from the current text selection in the PDF.
// Shared by mouse drag (HTML5 dragstart) and touch/pen drag (pdf-touch-drag-start).
export function buildTextSnippetFromSelection(containerRef, zoomLevel = 1, sourcePdfId = null) {
  const container = containerRef.current;
  if (!container) return null;

  const selection = window.getSelection();
  const selectionDetails = getPdfSelectionDetails(selection, containerRef, {
    current: container.querySelector(".pdf-zoom-content") || container,
  });
  if (!selectionDetails?.selectedText) return null;

  const { rect, pageNum, canvas, canvasRect, selectedText: text } = selectionDetails;
  if (!canvas || !canvasRect) {
    console.warn("Canvas not found for text drag coordinate calculation");
    return null;
  }

  const zoomScale = zoomLevel;
  const shouldAttachOcrImage = !DEVANAGARI_RE.test(text);

  return {
    id: Date.now(),
    type: "text", // Crucial: 'text' type
    text: text,
    // Store coordinates relative to the PAGE CANVAS in logical pixels
    x: (rect.left - canvasRect.left) / zoomScale,
    y: (rect.top - canvasRect.top) / zoomScale,
    width: rect.width / zoomScale,
    height: rect.height / zoomScale,
    // Store normalized coordinates
    xPct: (rect.left - canvasRect.left) / canvasRect.width,
    yPct: (rect.top - canvasRect.top) / canvasRect.height,
    widthPct: rect.width / canvasRect.width,
    heightPct: rect.height / canvasRect.height,
    pageNum,
    fromPDF: true,
    ...(shouldAttachOcrImage ? { ocrImage: buildSelectionOcrImage(canvas, rect, canvasRect) } : {}),
    ...(sourcePdfId != null && { pdf_id: sourcePdfId }),
  };
}

export function handleTextDragStart(e, containerRef, mode, zoomLevel = 1, sourcePdfId = null) {
  if (mode !== "select") return;

  const snippet = buildTextSnippetFromSelection(containerRef, zoomLevel, sourcePdfId);
  if (!snippet) return;

  // For mouse drag
  if (e.dataTransfer) {
    e.dataTransfer.setData("application/json", JSON.stringify(snippet));
    e.dataTransfer.effectAllowed = "copy";
  }
}

// Starts the custom (non-HTML5) drag used for touch and pen. WebKit on iPad does
// not reliably support HTML5 drag-and-drop with custom data, so touch input
// always goes through this path; initGlobalTouchDrag (Workspace) handles the drop.
// sourceEl: the element the finger went down on. If it gets removed mid-drag
// (e.g. the selection popup closes), WebKit keeps sending touch events to it but
// they no longer bubble to window — so the drag listens on it directly as well.
export function startTouchSnippetDrag(snippet, clientX, clientY, sourceEl = null) {
  if (!snippet) return;
  window.dispatchEvent(new CustomEvent("pdf-touch-drag-start", {
    detail: { snippet, startX: clientX, startY: clientY, sourceEl }
  }));
}


// --------------------- Image Drag ---------------------
// ✂️ attachImageDragHandler: Handles dragging for a "cropped" image snippet.
// Called from: pdfImageSelection.js -> handleImageSelection
export function attachImageDragHandler(imgEl, snippet, mode) {
  // iPad / WebKit: preventDefault() on pointerdown does NOT stop panning — only
  // touch-action does. Without it Safari starts scrolling and fires pointercancel,
  // killing the drag. Also suppress iOS long-press "Save Image" menu / system drag.
  imgEl.style.touchAction = "none";
  imgEl.style.webkitTouchCallout = "none";
  imgEl.style.userSelect = "none";
  imgEl.style.webkitUserSelect = "none";
  // iPad only: turn off the system long-press image drag (our pointer drag replaces it).
  // NOT on desktop — there the mouse drag IS the native HTML5 drag (dragstart below).
  if (IS_APPLE_TOUCH) imgEl.style.webkitUserDrag = "none";

  imgEl.addEventListener("dragstart", (e) => {
    if (mode !== "select") {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("application/json", JSON.stringify(snippet));
    const dragImg = new Image();
    dragImg.src = snippet.src;
    e.dataTransfer.setDragImage(dragImg, snippet.width / 2, snippet.height / 2);
  });

  // Touch + pen drag via Pointer Events (works for touch AND Wacom stylus)
  let pointerDragActive = false;

  imgEl.addEventListener("pointerdown", (e) => {
    if (mode !== "select") return;
    if (e.pointerType === 'mouse') return; // Mouse uses native dragstart above
    e.preventDefault(); // Prevent browser default pen pan / ink behavior
    e.stopPropagation();
    pointerDragActive = true;
    imgEl._ptrStartX = e.clientX;
    imgEl._ptrStartY = e.clientY;
    imgEl.setPointerCapture(e.pointerId);
  }, { passive: false });

  imgEl.addEventListener("pointermove", (e) => {
    if (!pointerDragActive) return;
    e.preventDefault();
    const dx = e.clientX - imgEl._ptrStartX;
    const dy = e.clientY - imgEl._ptrStartY;
    // Pen needs a smaller threshold (3px) vs touch (10px) since stylus is more precise
    const threshold = e.pointerType === 'pen' ? 3 : 10;
    if (Math.abs(dx) > threshold || Math.abs(dy) > threshold) {
      startTouchSnippetDrag(snippet, e.clientX, e.clientY, imgEl);
      pointerDragActive = false;
    }
  }, { passive: false });

  imgEl.addEventListener("pointerup", () => { pointerDragActive = false; });
  imgEl.addEventListener("pointercancel", () => { pointerDragActive = false; });
}

/**
 * Initializes global touch drag listeners for PDF images.
 * This handles the "ghost" drag element and drop logic on the Workspace.
 * 
 * @param {Function} addSnippet - Function to add the dropped snippet to workspace
 * @param {Function} screenToWorld - Helper to convert screen coords to world coords
 * @param {Object} workspaceRef - Ref to the workspace container to check drop bounds
 * @returns {Function} cleanup - Function to remove the global listener
 */
export function initGlobalTouchDrag(addSnippet, screenToWorld, workspaceRef) {
  const handleTouchDragStart = (e) => {
    const { snippet, startX, startY, sourceEl } = e.detail;
    if (!snippet) return;

    // Create Ghost Element — image preview for image snippets, text card for text
    let ghost;
    if (snippet.src) {
      ghost = document.createElement('img');
      ghost.src = snippet.src;
      ghost.style.width = `${snippet.width}px`;
    } else {
      ghost = document.createElement('div');
      const preview = String(snippet.text || '');
      ghost.textContent = preview.length > 140 ? `${preview.slice(0, 140)}…` : preview;
      Object.assign(ghost.style, {
        background: '#fff', color: '#222', font: '13px/1.4 -apple-system, sans-serif',
        padding: '8px 10px', borderRadius: '6px', boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
        width: '220px', whiteSpace: 'normal', overflow: 'hidden', maxHeight: '120px',
      });
    }
    ghost.style.position = 'fixed';
    ghost.style.left = `${startX}px`;
    ghost.style.top = `${startY}px`;
    ghost.style.maxWidth = '200px';
    ghost.style.pointerEvents = 'none';
    ghost.style.zIndex = '9999';
    ghost.style.opacity = '0.8';
    ghost.style.border = '2px dashed #007bff';
    ghost.style.transform = 'translate(-50%, -50%)';
    document.body.appendChild(ghost);

    let dropped = false; // guard against double-drop (touch fires both touchend + pointerup)

    const commitDrop = (clientX, clientY) => {
      if (dropped) return;
      dropped = true;
      const workspaceRect = workspaceRef.current?.getBoundingClientRect();
      if (workspaceRect &&
        clientX >= workspaceRect.left &&
        clientX <= workspaceRect.right &&
        clientY >= workspaceRect.top &&
        clientY <= workspaceRect.bottom) {
        const dropPos = screenToWorld(clientX, clientY);
        if (addSnippet) {
          addSnippet(snippet, dropPos);
          if (navigator.vibrate) navigator.vibrate(50);
        }
      }
      ghost.remove();
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
      sourceEl?.removeEventListener('touchmove', handleTouchMove);
      sourceEl?.removeEventListener('touchend', handleTouchEnd);
      sourceEl?.removeEventListener('touchcancel', handleTouchEnd);
      window.removeEventListener('pointermove', handlePenMove);
      window.removeEventListener('pointerup', handlePenUp);
      window.removeEventListener('pointercancel', handlePenUp);
    };

    // ✋ Touch handlers
    const handleTouchMove = (tm) => {
      // Stop Safari from scrolling the PDF / page underneath while dragging
      if (tm.cancelable) tm.preventDefault();
      const t = tm.touches[0];
      ghost.style.left = `${t.clientX}px`;
      ghost.style.top = `${t.clientY}px`;
    };

    const handleTouchEnd = (te) => {
      const t = te.changedTouches[0];
      if (te.type === 'touchcancel') { // iOS cancelled the gesture — don't drop
        dropped = true;
        ghost.remove();
        cleanup();
        return;
      }
      commitDrop(t.clientX, t.clientY);
    };

    // ✏️ Pen/stylus handlers — pen fires pointermove/pointerup, NOT touchmove/touchend
    const handlePenMove = (pm) => {
      if (pm.pointerType === 'touch') return; // touch handled above
      ghost.style.left = `${pm.clientX}px`;
      ghost.style.top = `${pm.clientY}px`;
    };

    const handlePenUp = (pu) => {
      if (pu.pointerType === 'touch') return; // touch handled above
      commitDrop(pu.clientX, pu.clientY);
    };

    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);
    sourceEl?.addEventListener('touchmove', handleTouchMove, { passive: false });
    sourceEl?.addEventListener('touchend', handleTouchEnd);
    sourceEl?.addEventListener('touchcancel', handleTouchEnd);
    window.addEventListener('pointermove', handlePenMove);
    window.addEventListener('pointerup', handlePenUp);
    window.addEventListener('pointercancel', handlePenUp);
  };

  window.addEventListener('pdf-touch-drag-start', handleTouchDragStart);

  // Return cleanup function
  return () => {
    window.removeEventListener('pdf-touch-drag-start', handleTouchDragStart);
  };
}
